import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import KpiCard from "@/components/KpiCard";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { LineChart, Line, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";
import {
  Users, UserPlus, Send, MessageCircle, TrendingUp, BarChart3, CheckCheck, Inbox, Activity, Loader2,
  Clock, CheckCircle2, Archive, AlertOctagon,
} from "lucide-react";
import { Link } from "react-router-dom";

const eventIcon = {
  contact_created: UserPlus,
  contact_updated: Users,
  tag_added: Users,
  broadcast_sent: Send,
  broadcast_delivered: CheckCheck,
  message_sent: MessageCircle,
  message_received: Inbox,
  flow_started: Activity,
  flow_completed: Activity,
  note_added: Activity,
};

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [activity, setActivity] = useState([]);
  const [growth, setGrowth] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get("/dashboard/stats"),
      api.get("/dashboard/activity"),
      api.get("/reports/contacts-growth?days=30"),
    ]).then(([s, a, g]) => {
      setStats(s.data);
      setActivity(a.data);
      setGrowth(g.data.series || []);
    });
  }, []);

  if (!stats) {
    return (
      <div className="p-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
    );
  }

  return (
    <div className="p-6 md:p-8 max-w-[1500px] mx-auto" data-testid="dashboard-page">
      <PageHeader
        title="Dashboard"
        description="Overview of your contacts, campaigns and conversations"
        testId="dashboard-header"
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard testId="kpi-total-contacts" label="Total contacts" value={stats.total_contacts} icon={Users} />
        <KpiCard testId="kpi-total-leads" label="Total leads" value={stats.total_leads} icon={UserPlus} />
        <KpiCard testId="kpi-messages-sent" label="Messages sent" value={stats.total_messages_sent} icon={Send} />
        <KpiCard testId="kpi-total-broadcasts" label="Broadcasts" value={stats.total_broadcasts} icon={MessageCircle} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
        <KpiCard testId="kpi-delivery-rate" label="Delivery rate" value={`${stats.delivery_rate}%`} icon={CheckCheck} />
        <KpiCard testId="kpi-reply-rate" label="Reply rate" value={`${stats.reply_rate}%`} icon={Inbox} />
        <KpiCard testId="kpi-conversion-rate" label="Conversion rate" value={`${stats.conversion_rate}%`} icon={TrendingUp} />
        <KpiCard testId="kpi-total-replies" label="Total replies" value={stats.total_replies} icon={BarChart3} />
      </div>

      {/* Conversation lifecycle metrics (Inbox v2) */}
      <div className="mt-6">
        <div className="label-caps mb-3">Inbox lifecycle</div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <KpiCard testId="kpi-conv-unread"   label="Unread"   value={stats.conversations?.unread   ?? 0} icon={Inbox} accent="text-brand-dark" />
          <KpiCard testId="kpi-conv-active"   label="Active"   value={stats.conversations?.active   ?? 0} icon={MessageCircle} accent="text-sky-600" />
          <KpiCard testId="kpi-conv-waiting"  label="Waiting"  value={stats.conversations?.waiting  ?? 0} icon={Clock} accent="text-amber-600" />
          <KpiCard testId="kpi-conv-resolved" label="Resolved" value={stats.conversations?.resolved ?? 0} icon={CheckCircle2} accent="text-emerald-600" />
          <KpiCard testId="kpi-conv-archived" label="Archived" value={stats.conversations?.archived ?? 0} icon={Archive} accent="text-slate-500" />
          <KpiCard testId="kpi-conv-spam"     label="Spam"     value={stats.conversations?.spam     ?? 0} icon={AlertOctagon} accent="text-red-600" />
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-6">
        <Card className="lg:col-span-2 border-slate-200 shadow-none">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="label-caps">Contacts growth (last 30 days)</div>
                <div className="font-heading text-xl font-semibold mt-1 text-slate-900">
                  +{growth.reduce((a, b) => a + b.count, 0)} new contacts
                </div>
              </div>
            </div>
            <div className="h-64" style={{ minHeight: 256, minWidth: 0 }}>
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={256}>
                <LineChart data={growth} margin={{ top: 8, right: 8, bottom: 0, left: -16 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748B" }} tickFormatter={(d) => d?.slice(5)} />
                  <YAxis tick={{ fontSize: 11, fill: "#64748B" }} allowDecimals={false} />
                  <Tooltip contentStyle={{ borderRadius: 8, border: "1px solid #E2E8F0", fontSize: 12 }} />
                  <Line type="monotone" dataKey="count" stroke="#25D366" strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200 shadow-none">
          <CardContent className="p-6">
            <div className="label-caps mb-4">Recent activity</div>
            {activity.length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">No activity yet</div>
            ) : (
              <ul className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {activity.map((e) => {
                  const Icon = eventIcon[e.event_type] || Activity;
                  return (
                    <li key={e.id} className="flex items-start gap-3" data-testid={`activity-item-${e.id}`}>
                      <div className="mt-0.5 p-1.5 rounded-md bg-slate-100 text-slate-600">
                        <Icon className="w-3.5 h-3.5" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-sm text-slate-800 truncate">{e.title}</div>
                        <div className="text-xs text-slate-400">{new Date(e.created_at).toLocaleString()}</div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { to: "/contacts", label: "Add contacts", desc: "Grow your list", color: "bg-slate-100 text-slate-700" },
          { to: "/broadcasts", label: "Create broadcast", desc: "Send messages at scale", color: "bg-brand-light text-brand-dark" },
          { to: "/flows", label: "Build automation", desc: "Set up flows", color: "bg-violet-50 text-violet-700" },
        ].map((card) => (
          <Link
            key={card.to}
            to={card.to}
            data-testid={`quick-action-${card.to.slice(1)}`}
            className="block group"
          >
            <Card className="border-slate-200 shadow-none hover:border-brand-dark transition-colors">
              <CardContent className="p-5">
                <Badge variant="secondary" className={`${card.color} border-0`}>Quick action</Badge>
                <div className="mt-3 font-heading font-semibold text-slate-900">{card.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{card.desc}</div>
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>
    </div>
  );
}
